import React, {memo, useEffect, useMemo, useRef, useState} from "react";
import {getLocale, history, useIntl, useParams} from "umi";
import classNames from "classnames";
import {useDebounceFn, useMemoizedFn} from "ahooks";
import {Bubble, Dropdown, DropdownBox, Icon, List, Popover} from "tea-component";
import SvgIcon from "@home/components/SvgIcon";
import TimeCompare from "@/pages/datalab/components/DateComponents/TimeCompare";
import { formatDateRange, getDateRange, type TRelativeTime } from "@dbl/core/date";
import { isEmpty } from "lodash-es";
import {
  getScopeFilters,
  hasActiveCompareTime,
} from '@/pages/datalab/components/DashboardLayout/components/DragShell/_utils';
import {
  DownLoadModalContent,
  openCommonDataLabDownloadPage
} from "@/pages/datalab/pages/Multidimensional/Analysis/components/AnalysisLineChart";
import ConfirmModal from "@/pages/aigc/pages/Text2SQL/components/RelevantTable/components/ConfirmModal";
import {LANGUAGE_EN} from "@home/_config/config";
import { getLocalText } from '@/pages/datalab/utils/utils';
import {getLocaleState} from "@home/utils/state";
import {handleExportClick} from "@/pages/datalab/components/DashboardLayout/_utils";
import ShowFilterContentIcon, {
  FILTER_CONTENT_KEY
} from "@/pages/datalab/components/ChartCardIconButtons/ShowFilterContentIcon";
import { useClosePopoversWhenAnchorHidden } from './useClosePopoversWhenAnchorHidden';
import { getCardOverlayProps } from './getCardOverlayProps';
import BubbleEllipsis from "@dbl/components/BubbleEllipsis";

export const DownloadModal = (props: any) => {
  const {downloadParams, dataMsg, isEn, formatMessage, open, closeDownloadModal, ...restProps } = props;

  const [config, setConifg] = useState({
    rowLimitValue: 2000,
    fileFormat: 'excel',
  });
  
  const {run: handleOnDownLoadModal}=useDebounceFn(
    () => {
      const urlParams = history?.location?.query || {};
      openCommonDataLabDownloadPage({
        ...(downloadParams ?? {}),
        language: getLocale(),
        page_size: Number(urlParams?.rowLimit || urlParams?.rowlimit || urlParams?.row_limit) || config.rowLimitValue,
        is_download: true,
        max_size: Number(urlParams?.rowLimit || urlParams?.rowlimit || urlParams?.row_limit) || config.rowLimitValue,
        "file_name": getLocalText(isEn, dataMsg?.name_cn, dataMsg?.name)?.replace(/[/%]/g, '_') || '',
        file_format: config.fileFormat ?? 'excel',
        // 后端计算动态日期时间需要的
        request_time: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },'/api/v1/datalab/getChartAnalysis').finally(() => {
        closeDownloadModal();
        setConifg({
          rowLimitValue: 2000,
          fileFormat: 'excel',
        });
      });
    },{
      wait: 500
    }
  );
  
  return (<ConfirmModal
    {...{
      visible: open,
      caption: formatMessage({ id: 'dbl_chart_modal_download_set' }),
      click: handleOnDownLoadModal,
      clone: closeDownloadModal,
      size: 580,
    }}
    {...restProps}
  >
    <DownLoadModalContent
      {...{
        radioValue: config.rowLimitValue,
        handleOnRadioChange: (value) =>
          setConifg(prev => ({...prev, ...value})),
      }}
    />
  </ConfirmModal>);
};

const DragCardHeader = (props: any) => {
  const {isView, showVs, title, isOverLimit, dataMsg, loading, error, downloadParams, showDownloadLimit, filters, filterValues, userFilterInfo, hasChartPrivilege, jumpToEdit, deleteCard, system, chartExplore, isFullScreen, openFullscreen , fullScreenContent, tableSortProps, compareTimeData, requestTableData, chartSwitchNode, onCompareTimeFiltersChange, previewSQL} = props;
  const {gamecode}: any = useParams();
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [open, setOpen] = useState(false);
  const [openDownload, setOpenDownload] = useState(false);
  const [openVs, setOpenVs] = useState(false);
  const [openSort, setOpenSort] = useState(false);
  const sortOverlayLeaveTimerRef = useRef<number | null>(null);
  const [previewMoreEntryEl, setPreviewMoreEntryEl] = useState<HTMLDivElement | null>(null);
  // const [forceVisible, setForceVisible] = useState(!!onForceClose);
  const scopeFilters = getScopeFilters(filters || [], Object.values(filterValues || {}));

  const isTimeCompareActive = useMemo(
    () => hasActiveCompareTime(compareTimeData, filterValues),
    [compareTimeData, filterValues],
  );

  const clearSortOverlayLeaveTimer = useMemoizedFn(() => {
    if (sortOverlayLeaveTimerRef.current != null) {
      window.clearTimeout(sortOverlayLeaveTimerRef.current);
      sortOverlayLeaveTimerRef.current = null;
    }
  });

  useEffect(() => () => clearSortOverlayLeaveTimer(), [clearSortOverlayLeaveTimer]);

  /** 预览态三点菜单：时间对比打开时，鼠标移出菜单也不收起；排序弹窗随主菜单关闭而关 */
  const handlePreviewDropdownOpenChange = useMemoizedFn((nextOpen: boolean) => {
    if (nextOpen) {
      setOpen(true);
      fullScreenContent?.current?.classList.add('dbl-show-card-icon');
      return;
    }
    if (openVs || openSort) {
      setOpen(true);
      clearSortOverlayLeaveTimer();
      fullScreenContent?.current?.classList.add('dbl-show-card-icon');
      return;
    }
    setOpen(false);
    setOpenSort(false);
    clearSortOverlayLeaveTimer();
    fullScreenContent?.current?.classList.remove('dbl-show-card-icon');
  });

  const closePreviewPopovers = useMemoizedFn(() => {
    setOpenVs(false);
    setOpenSort(false);
    setOpen(false);
    clearSortOverlayLeaveTimer();
    fullScreenContent?.current?.classList.remove('dbl-show-card-icon');
  });

  const closeTimeCompareAndMenu = closePreviewPopovers;

  useClosePopoversWhenAnchorHidden(
    previewMoreEntryEl,
    isView && (open || openVs || openSort),
    closePreviewPopovers,
  );
  
  const downLoadFunc = useMemoizedFn(() => {
    if (showDownloadLimit) {
      setOpenDownload(true);
    } else {
    
    }
  });
  
  const overlayProps = getCardOverlayProps(
    Boolean(isFullScreen),
    fullScreenContent?.current,
  );

  // 增加icon需要关注全屏模式下弹窗类型节点的挂载方式
  return (<><div className="dbl-card-drag-flex">
    <div className="dbl-card-drag-title">
      <div className="dbl-card-drag-title__row">
        {!isView && <SvgIcon type="drag-icon"/>}
        <span className="dbl-card-drag-title__text">
          {title}
            
        </span>
        
        
        
      </div>
    </div>
    { isView && isOverLimit && (<div className="dbl-card-drag-title__info">
      <Icon type="warning" className="dbl-card-drag-title__info-icon"/>
      <BubbleEllipsis
        dark
        content={formatMessage({ id: 'dbl_chart_row' })}
        overlayClassName="dbl-card-drag-title__info-bubble"
        ellipsisClassName="dbl-card-drag-title__info-text"
      />

    </div>)}
    <div className={classNames('dbl-card-icon-operation-box', isView && 'dbl-card-preview-toolbar')}>
      {
        (!loading && !error && !isFullScreen) ? (<Bubble
          content={formatMessage({id: 'dbl_common_fullscreen'})}
          dark
        >
          <div
            className={classNames('dbl-card-icon-download', {'dbl-card-icon-disabled': isFullScreen === undefined })}
            onClick={() => {
              if (isFullScreen === undefined) return;
              getLocaleState('datalabMessageBus')?.notifyTableFullscreenClick({operation: 'fullScreen'});
              openFullscreen(true);
            }}
          >
            <SvgIcon type={"fullscreen"}/>
          </div>
        </Bubble>) : ''
      }
      {
        isView && (!loading && !error) ? (
          <Dropdown
            trigger="hover"
            clickClose={false}
            boxClassName={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-more-menu')}
            button={
              <div
                ref={setPreviewMoreEntryEl}
                className={classNames('dbl-card-drag-operation', 'dbl-card-preview-more-entry', {action: open})}
              >
                <SvgIcon type="there-points"/>
              </div>
            }
            placementOffset={0}
            open={open}
            appearance="pure"
            placement="bottom-end"
            onOpenChange={handlePreviewDropdownOpenChange}
            {...overlayProps}
          >
            <List type="option">
              {chartSwitchNode ? (
                <List.Item className="dbl-card-preview-chart-switch-menu-item">
                  {chartSwitchNode}
                </List.Item>
              ) : null}
              {showVs && compareTimeData?.filterId ? (
                <Popover
                  trigger="click"
                  placement="right-start"
                  visible={openVs}
                  onVisibleChange={(vsOpen) => {
                    if (vsOpen) {
                      setOpenVs(true);
                      setOpenSort(false);
                      clearSortOverlayLeaveTimer();
                      fullScreenContent?.current?.classList.add('dbl-show-card-icon');
                    }
                    // 不响应 false：仅能通过确定/取消关闭，避免移出菜单或误点外部被收起
                  }}
                  {...overlayProps}
                  overlay={
                    <DropdownBox
                      className={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-timecompare-dropdown')}
                      preventMouseDown
                    >
                      <TimeCompare
                        openSingle={true}
                        defaultCompareTimes={compareTimeData.defaultCompareTimes}
                        baseTimeRange={compareTimeData.baseTimeRange}
                        baseTimeName={compareTimeData.baseTimeName}
                        timeGranularity={compareTimeData.timeGranularity}
                        dropdownProps={overlayProps}
                        popoverProps={overlayProps}
                        onConfirm={(compareTimes: TRelativeTime[]) => {
                          let newCompareTimes: TRelativeTime[] = [];
                          if (compareTimes && compareTimes.length > 0) {
                            newCompareTimes = compareTimes.map((compare) => {
                              if (compare?.time_range && isEmpty(compare.last_time)) return compare;
                              const {last_time: lastTime, granularity} = compare || ({} as any);
                              const range = getDateRange(compareTimeData.baseTimeRange || [], {
                                amount: lastTime,
                                granularity,
                                timeGranularity: compareTimeData.timeGranularity,
                              });
                              return {
                                ...compare,
                                time_range: formatDateRange(range, compareTimeData.timeGranularity),
                              } as any;
                            });
                          }

                          if (compareTimeData?.chartFilter?.filter_name) {
                            const newFilters = filterValues?.map((item: any) => {
                              if (item.filterKey === compareTimeData.filterId) {
                                return {
                                  ...item,
                                  compare_time: newCompareTimes
                                };
                              }
                              return item;
                            });
                            if (onCompareTimeFiltersChange) {
                              onCompareTimeFiltersChange?.(newFilters);
                            } else {
                              requestTableData({filters: newFilters});
                            }
                          } else {
                            const scopeCharts = filterValues?.find((item: any) => item?.filterKey === compareTimeData?.filterId)?.scope_charts || [];
                            const nextFilters = [
                              ...(filterValues || []),
                              {
                                ...(compareTimeData?.chartFilter || {}),
                                compare_time: newCompareTimes,
                                scope_charts: scopeCharts,
                              }
                            ];
                            if (onCompareTimeFiltersChange) {
                              onCompareTimeFiltersChange?.(nextFilters);
                            } else {
                              requestTableData({filters: nextFilters});
                            }
                          }

                          closeTimeCompareAndMenu();
                        }}
                        onCancel={closeTimeCompareAndMenu}
                      />
                    </DropdownBox>
                  }
                >
                  <List.Item
                    className={classNames(
                      'dbl-card-preview-vs-menu-item',
                      isTimeCompareActive && 'dbl-card-preview-vs-menu-item--active',
                    )}
                  >
                    <SvgIcon type="vs-icon"/><span className="dbl-card-preview-vs-menu-text">{formatMessage({id: 'dbl_time_compare_title'})}</span>
                  </List.Item>
                </Popover>
              ) : null}
              <List.Item className="dbl-card-preview-filter-menu-item">
                <ShowFilterContentIcon
                  contentMap={{
                    [FILTER_CONTENT_KEY.GENERAL_FILTER]: { filters: scopeFilters},
                    [FILTER_CONTENT_KEY.USER_FILTER]: userFilterInfo,
                  }}
                  isEdit={true}
                  overlayClassName="dbl-card-preview-more-filter-overlay"
                  triggerEndContent={
                    <span className="dbl-card-preview-more-menu-label">
                      {formatMessage({id: 'dbl_filter_tab_current_filter'})}
                    </span>
                  }
                  onVisibleChange={(visible: boolean) => {
                    if (visible) {
                      fullScreenContent?.current?.classList.add('dbl-show-card-icon');
                    } else {
                      fullScreenContent?.current?.classList.remove('dbl-show-card-icon');
                    }
                  }}
                  {...overlayProps}
                />
              </List.Item>
              <Popover
                trigger="click"
                placement="bottom-end"
                visible={openSort}
                onVisibleChange={(sortOpen) => {
                  setOpenSort(sortOpen);
                  if (sortOpen) {
                    setOpenVs(false);
                    clearSortOverlayLeaveTimer();
                    fullScreenContent?.current?.classList.add('dbl-show-card-icon');
                  } else {
                    clearSortOverlayLeaveTimer();
                    fullScreenContent?.current?.classList.remove('dbl-show-card-icon');
                  }
                }}
                {...overlayProps}
                overlay={
                  <div
                    onMouseEnter={() => clearSortOverlayLeaveTimer()}
                    onMouseLeave={() => {
                      clearSortOverlayLeaveTimer();
                      sortOverlayLeaveTimerRef.current = window.setTimeout(() => {
                        sortOverlayLeaveTimerRef.current = null;
                        setOpenSort(false);
                        fullScreenContent?.current?.classList.remove('dbl-show-card-icon');
                      }, 180);
                    }}
                  >
                    <DropdownBox
                      className={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-more-menu')}
                      preventMouseDown
                    >
                    <List type="option">
                      <List.Item
                        onClick={() => {
                          tableSortProps?.onReset?.();
                          setOpenSort(false);
                        }}
                      >
                        {formatMessage({id: 'dbl_common_sort_reset'})}
                      </List.Item>
                      <List.Item
                        onClick={() => {
                          tableSortProps?.onClear?.();
                          setOpenSort(false);
                        }}
                      >
                        {formatMessage({id: 'dbl_common_sort_clear'})}
                      </List.Item>
                    </List>
                  </DropdownBox>
                  </div>
                }
              >
                <List.Item className="dbl-card-preview-sort-menu-item">
                  <SvgIcon type="refresh-icon-type"/>
                  {formatMessage({id: 'dbl_common_sort'})}
                </List.Item>
              </Popover>
              {
                chartExplore && (dataMsg.type === 0) ? (
                  <List.Item
                    className="dbl-card-preview-explore-menu-item"
                    onClick={() => {
                      handleExportClick({
                        filters,
                        system,
                        gamecode,
                        dataMsg,
                        userFilterInfo,
                      });
                      setOpen(false);
                    }}
                  >
                    <SvgIcon type="explore"/>
                    {formatMessage({id: 'dbl_create_dashboard_icon_explore'})}
                  </List.Item>
                ) : null
              }
              {previewSQL ? (
                <List.Item
                  onClick={() => {
                    previewSQL();
                    setOpen(false);
                  }}
                >
                  <SvgIcon type="open-eyes"/>
                  {formatMessage({id: 'dbl_multi_preview_sql'})}
                </List.Item>
              ) : null}
              <List.Item
                className="dbl-card-preview-download-menu-item"
                onClick={() => {
                  downLoadFunc();
                  setOpen(false);
                }}
              >
                <SvgIcon type="download"/>
                {formatMessage({id: 'dbl_common_download'})}
              </List.Item>
            </List>
          </Dropdown>
        ) : null
      }
      {
        !isView ? (<Dropdown
          trigger="hover"
          clickClose={false}
          boxClassName={classNames('dbl-card-drag-operation-dropdown', 'dbl-card-preview-more-menu')}
          button={
            <div className={classNames('dbl-card-drag-operation', 'dbl-card-preview-more-entry', {action: open})}>
              <SvgIcon type="there-points"/>
            </div>
          }
          footer={ (!loading && !error) ?
            (<div className={'dbl-card-icon-download'} onClick={downLoadFunc}>
              <SvgIcon type="download"/>{formatMessage({id: 'dbl_common_download'})}
            </div>)
            : ''
          }
          placementOffset={0}
          open={open}
          appearance="pure"
          placement="bottom-end"
          onOpenChange={(value) => {
            setOpen(value);
          }}
          {...overlayProps}
        >
          <List type="option">
            {(!loading && !error && chartSwitchNode) ? (
              <List.Item className="dbl-card-preview-chart-switch-menu-item">
                {chartSwitchNode}
              </List.Item>
            ) : null}
            {
              (!loading && !error) ? (
                <List.Item className="dbl-card-preview-filter-menu-item">
                  <ShowFilterContentIcon
                    contentMap={{
                      [FILTER_CONTENT_KEY.GENERAL_FILTER]: { filters: scopeFilters},
                      [FILTER_CONTENT_KEY.USER_FILTER]: userFilterInfo,
                    }}
                    isEdit={true}
                    overlayClassName="dbl-card-preview-more-filter-overlay"
                    triggerEndContent={
                      <span className="dbl-card-preview-more-menu-label">
                        {formatMessage({id: 'dbl_filter_tab_current_filter'})}
                      </span>
                    }
                    onVisibleChange={(visible: boolean) => {
                      if (visible) {
                        fullScreenContent?.current?.classList.add('dbl-show-card-icon');
                      } else {
                        fullScreenContent?.current?.classList.remove('dbl-show-card-icon');
                      }
                    }}
                    {...overlayProps}
                  />
                </List.Item>
              ) : null
            }
            {
              (hasChartPrivilege && dataMsg?.edit) ? (<List.Item
                onClick={() => {
                  jumpToEdit();
                  setOpen(false);
                }}
              >
                <SvgIcon type="pencil-m"/>{formatMessage({id: 'dbl_common_edit'})}
              </List.Item>
              ) : (
                ''
              )}
            {previewSQL ? (
              <List.Item
                onClick={() => {
                  previewSQL();
                  setOpen(false);
                }}
              >
                <SvgIcon type="open-eyes"/>
                {formatMessage({id: 'dbl_multi_preview_sql'})}
              </List.Item>
            ) : null}
            <List.Item
              onClick={() => {
                setOpen(false);
                deleteCard(dataMsg);
              }}
            >
              <SvgIcon type="trash-can-icon"/>{formatMessage({id: 'dbl_common_delete'})}
            </List.Item>
          </List>
        </Dropdown>) : null
      }
      {
        isFullScreen ? (<div
          className={'dbl-card-icon-close'}
          onClick={() => {
            if (isFullScreen === undefined) return;
            getLocaleState('datalabMessageBus')?.notifyTableFullscreenClick({operation: 'notFullScreen'});
            openFullscreen(false);
          }}
        >
          <SvgIcon type={"clonse"}/>
        </div>) : ''
      }
  
    </div>
  </div>
  {
    showDownloadLimit ? (<DownloadModal
      downloadParams={downloadParams} 
      dataMsg={dataMsg} 
      isEn={isEn} 
      formatMessage={formatMessage}
      open={openDownload} 
      closeDownloadModal={() => setOpenDownload(false)}
      {...(isFullScreen ? {
        popupContainer: fullScreenContent?.current,
        positionFixed: false,
      } : {})}
    />) : ''
  }
  </>);
};

export default memo(DragCardHeader);